// ConsequenceConfirm — destructive confirm that states the consequence in
// plain language (Agreement Command Center design language: blocked is a
// statement, and so is what a destructive action will do).
//
// Composed on the DS `.modal` primitive exactly like ConfirmDialog (portal to
// document.body, focus trap, Escape + scrim = cancel, inert background). Focus
// lands on Cancel so a stray Enter never destroys anything.

import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '@/lib/useFocusTrap';
import { useEscapeKey } from '@/lib/useEscapeKey';
import { useInertBackground } from '@/lib/useInertBackground';

export interface ConsequenceConfirmProps {
  open: boolean;
  title: string;
  /** The consequence, in plain language — what happens if you confirm. */
  consequence: ReactNode;
  confirmLabel: string;
  tone?: 'danger' | 'default';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConsequenceConfirm({
  open,
  title,
  consequence,
  confirmLabel,
  tone = 'default',
  busy = false,
  onConfirm,
  onCancel,
}: ConsequenceConfirmProps): React.JSX.Element | null {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const containerRef = useFocusTrap(open);

  const handleEscape = useCallback(() => {
    if (!busy) onCancel();
  }, [busy, onCancel]);
  useEscapeKey(handleEscape, open);
  useInertBackground(open);

  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  const titleId = 'consequence-confirm-title';
  const descId = 'consequence-confirm-desc';

  const dialog = (
    <>
      <div
        className="modal-backdrop"
        onClick={() => {
          if (!busy) onCancel();
        }}
      />
      <div
        ref={containerRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="modal"
        data-size="sm"
      >
        <div className="modal-header">
          <h2 id={titleId} className="modal-title">
            {title}
          </h2>
        </div>
        <div className="modal-body">
          <p id={descId} className="modal-description">
            {consequence}
          </p>
        </div>
        <div className="modal-footer">
          <button
            ref={cancelRef}
            type="button"
            className="btn"
            data-variant="secondary"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            data-variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </>
  );

  return createPortal(dialog, document.body);
}
